import { useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";

import {
  AI_ACCENT_COLOR,
  type ResolvedStorageKeyPayload,
  useFocusedTag,
} from "src/reusable/context/FocusedTagContext";

interface StorageKeyLinkProps {
  payload: ResolvedStorageKeyPayload | null;
  rawBody?: string;
}

/**
 * Inline chip rendered for every <storage-key>...</storage-key> the
 * backend resolver lets through.
 *
 * Click behavior is pluggable via FocusedTagContext: inside the Equipment
 * AI page tree, the provider's requestFocus drives session-add and
 * pulse-scroll of the matching TagChartCell. Outside that tree (the
 * default no-op context), the chip still renders metadata on hover but
 * click does nothing — useful since chat messages can appear in places
 * that have no dashboard to scroll to.
 *
 * Pairs with the cite chip (<CitationLink>). Uses MUI primary blue so
 * it sits outside the nuclear health palette (red/yellow/green/orange)
 * and reads as a UI affordance, not a status indicator.
 */
const StorageKeyLink: React.FC<StorageKeyLinkProps> = ({
  payload,
  rawBody,
}) => {
  const { requestFocus, markAsTouched, boundSourceId, roundsSourceId } =
    useFocusedTag();

  // Every chip the resolver lets through marks its tag as AI-touched
  // (passive — fires regardless of whether the user ever clicks).
  // This is what lets the dashboard's persistent AI-blue stripe appear
  // on every referenced sensor between turns, and what makes the
  // "AI-referenced" view filter meaningful. markAsTouched gates on
  // source-id match, so foreign-source citations don't taint the
  // local set.
  //
  // Depends on the primitive (source_id, storage_key) identity rather
  // than the payload object — react-markdown re-builds the node tree
  // on every stream chunk, so the object identity isn't stable. With a
  // plain ``[payload]`` dep, this effect would re-fire for every chip
  // on every chunk on long responses. The setter is idempotent so the
  // visible state stays correct either way, but the function-call /
  // Set-membership cost adds up.
  const payloadSource = payload?.source_id;
  const payloadKey = payload?.storage_key;
  useEffect(() => {
    if (payload) markAsTouched(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadSource, payloadKey, markAsTouched]);

  // Resolver-stripped or malformed payload: render the body as a plain
  // chip so the user still sees something where the AI was pointing.
  if (!payload) {
    return (
      <Box
        component="span"
        data-testid="inline-storage-key-fallback"
        sx={fallbackChipSx}
      >
        ({rawBody || "sensor"})
      </Box>
    );
  }

  // sensor_metadata descriptions sometimes embed the storage_key in
  // trailing parens (e.g. "BRG OIL PRES #1 (TURB A) (U1_DFS276)") —
  // redundant once the chip already represents that storage_key, and
  // it produces nested parens since the chip wraps the whole label in
  // its own outer parens. Strip a trailing "(STORAGE_KEY)" if present.
  const rawLabel = payload.description || payload.storage_key;
  const trailingKey = payload.storage_key
    ? new RegExp(
        "\\s*\\(" +
          payload.storage_key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
          "\\)\\s*$",
      )
    : null;
  const label = trailingKey ? rawLabel.replace(trailingKey, "") : rawLabel;
  const unitsSuffix = payload.units ? ` (${payload.units})` : "";
  // Foreign = cites neither of the dashboard's bound sources. Must mirror
  // FocusedTagContext.isBoundSource: the rounds slot (roundsSourceId) is as
  // live as the PI slot — a rounds chip is a real dashboard control, and
  // rendering it with the inert gray treatment both lies about a click it
  // WOULD honor and reads as "something's wrong". (This chip previously
  // checked boundSourceId alone, which is exactly the bug that shipped:
  // rounds chips rendered gray and unclickable while the context happily
  // accepted their payloads.)
  const isForeignSource =
    boundSourceId != null &&
    payload.source_id !== boundSourceId &&
    !(roundsSourceId != null && payload.source_id === roundsSourceId);

  const hoverPreview = (
    <Box sx={{ p: 1.25, maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
        {unitsSuffix}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          fontFamily: "monospace",
        }}
      >
        {payload.storage_key}
      </Typography>
      {payload.source_name && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
        >
          Source: {payload.source_name}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          fontStyle: "italic",
          color: "text.secondary",
        }}
      >
        {isForeignSource
          ? "Different source — open separately to view"
          : "Click to focus on the dashboard"}
      </Typography>
    </Box>
  );

  // Foreign-source citation: the dashboard is bound to a single
  // TimeSeriesSource, so there's no canvas to plot this tag on and a
  // click can't do anything useful. Render it as muted inline metadata
  // rather than the blue pill — the pill reads as a live dashboard
  // control, and showing one for a tag that will never plot is the
  // dead-end the user hits (clicks, nothing happens). The interaction
  // was already disabled below; this also drops the affordance so the
  // chip stops advertising a click it won't honor. The tooltip still
  // carries the full metadata + an explanation of why it isn't live.
  if (isForeignSource) {
    return (
      <Tooltip
        title={hoverPreview}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: 2,
              "& .MuiTooltip-arrow": { color: "background.paper" },
            },
          },
        }}
      >
        <Box
          component="span"
          data-testid={`inline-storage-key-foreign-${payload.storage_key}`}
          data-attr={`assistant:link:storage-key-foreign-${payload.source_id}`}
          aria-label={`${label} (different source — not on this dashboard)`}
          sx={foreignChipSx}
        >
          ({label})
        </Box>
      </Tooltip>
    );
  }

  const handleClick = () => {
    requestFocus(payload);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  // Visual shape mirrors <CitationLink>: parens render inside the
  // blue pill (so "(description)" reads as a single linked phrase),
  // no leading icon. Same padding/radius/font tokens as the citation
  // chip so both citation flavors sit on the same baseline when
  // adjacent in prose.
  //
  // Only same-source citations reach here — foreign-source ones
  // early-return above as inert metadata. So this chip is always a
  // live dashboard control.
  //
  // a11y: rendered as a button-roled focusable with an aria-label
  // that names the action, plus a keyboard handler for Enter/Space.
  // :focus-visible gives keyboard users a visible focus ring that
  // doesn't appear on mouse-driven focus.
  const chip = (
    <Box
      component="span"
      data-testid={`inline-storage-key-${payload.storage_key}`}
      data-attr={`assistant:link:storage-key-${payload.source_id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Focus sensor ${label} on dashboard`}
      sx={{
        cursor: "pointer",
        color: AI_ACCENT_COLOR,
        backgroundColor: `${AI_ACCENT_COLOR}0F`,
        borderRadius: "12px",
        padding: "1px 6px",
        margin: "0 2px",
        textDecoration: "none",
        transition: "all 0.15s ease",
        "&:hover": {
          backgroundColor: `${AI_ACCENT_COLOR}1A`,
          transform: "translateY(-0.5px)",
        },
        "&:focus": { outline: "none" },
        "&:focus-visible": {
          outline: `2px solid ${AI_ACCENT_COLOR}`,
          outlineOffset: "2px",
        },
        fontSize: "0.85em",
        fontWeight: 450,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "baseline",
        lineHeight: 1.2,
      }}
    >
      ({label})
    </Box>
  );

  return (
    <Tooltip
      title={hoverPreview}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 2,
            "& .MuiTooltip-arrow": { color: "background.paper" },
          },
        },
      }}
    >
      {chip}
    </Tooltip>
  );
};

const fallbackChipSx = {
  display: "inline",
  fontSize: "0.85em",
  color: "text.disabled",
  fontStyle: "italic",
  margin: "0 2px",
};

// Inert treatment for foreign-source citations — muted text, no pill
// background or radius, default cursor. Deliberately distinct from the
// blue clickable pill so the user can tell at a glance which sensors are
// live dashboard controls and which are read-only references to another
// source. Dotted underline signals "has info on hover" without implying
// a click target.
const foreignChipSx = {
  display: "inline-flex",
  alignItems: "center",
  verticalAlign: "baseline",
  lineHeight: 1.2,
  cursor: "default",
  color: "text.secondary",
  borderBottom: "1px dotted",
  borderColor: "text.disabled",
  margin: "0 2px",
  fontSize: "0.85em",
  fontWeight: 450,
};

export default StorageKeyLink;
